// Winning Formula — the top wins-vs-losses gaps, paired labeled bars.
// Wins wear series green, losses wear red; both carry direct value labels
// (color never carries the meaning alone).

import React from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card } from './ui';
import type { WinningFormulaResult, FormulaGap } from '../../lib/winning-formula';

const GapRow = ({ gap }: { gap: FormulaGap }) => {
  const denom = Math.max(Math.abs(gap.winsValue), Math.abs(gap.lossesValue), 1e-9);
  const bar = (v: number) => `${Math.max(6, Math.round((Math.abs(v) / denom) * 100))}%`;
  return (
    <div className="px-4 py-3" style={{ borderTop: `1px solid ${GS.line}` }}>
      <p className="text-[11px] font-extrabold mb-1.5" style={{ color: GS.ink }}>{gap.label}</p>
      {([['Wins', gap.winsValue, '#0EA372', GS.greenInk], ['Losses', gap.lossesValue, GS.red, GS.red]] as const).map(([tag, v, barColor, tagColor]) => (
        <div key={tag} className="flex items-center gap-2 mt-1">
          <span className="w-11 text-[8px] font-black uppercase shrink-0" style={{ color: tagColor, letterSpacing: '0.08em' }}>{tag}</span>
          <div className="flex-1 h-2 rounded overflow-hidden" style={{ background: GS.panel2 }}>
            <div className="h-full rounded" style={{ width: bar(v), background: barColor }} />
          </div>
          <span className="w-11 text-right text-[11px] font-black shrink-0" style={{ color: GS.ink }}>
            {fmtStat(v, gap.kind)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const WinningFormulaCard = ({ formula }: { formula: WinningFormulaResult }) => {
  if (!formula.hasEnoughGames) {
    return (
      <Card className="p-5 text-center">
        <p className="text-sm font-black" style={{ color: GS.ink }}>Not enough games yet</p>
        <p className="text-[11px] font-semibold mt-1 leading-relaxed" style={{ color: GS.ink2 }}>
          Once you've logged at least 2 wins and 2 losses with stats, Dugout shows you what actually decides your games.
        </p>
      </Card>
    );
  }

  const top = formula.gaps.slice(0, 3);
  const headline = top[0];

  return (
    <Card>
      <div className="[&>div:first-child]:!border-t-0">
        {top.map(gap => <GapRow key={gap.key} gap={gap} />)}
      </div>
      <p className="px-4 py-3 text-[10px] font-semibold leading-relaxed" style={{ color: GS.ink3, borderTop: `1px solid ${GS.line}` }}>
        Your {formula.wins} wins vs your {formula.losses} losses, ranked by biggest gap.
        {headline?.key === 'bbInnAllowed' && <b style={{ color: GS.ink }}> Walks are your season.</b>}
      </p>
    </Card>
  );
};
